import { useOutletContext, useParams } from "react-router";
import { Button } from "@/components/Button";
import { TextInput } from "@/components/TextInput";
import { Toast } from "@/components/Toast";
import { useEntitySettings } from "@/lib/useEntitySettings";
import type { ConnectorLayoutContext } from "./ConnectorLayout";
import { LoadError } from "@/components/LoadError";

interface Connector {
  id: string;
  name: string;
}

export function ConnectorSettings() {
  const { orgId, connectorId } = useParams();
  const { onConnectorRenamed } = useOutletContext<ConnectorLayoutContext>();
  const settings = useEntitySettings<Connector>({
    entityId: connectorId,
    endpointSegment: "connectors",
    entityLabel: "connector",
    buildDeleteConfirmTitle: (name) => `Delete ${name}?`,
    buildDeleteConfirmMessage: () =>
      "Deleting this connector will revoke its token and remove the route it provides to assigned resources.",
    deleteSuccessMessage: (name) => `${name} deleted.`,
    afterDeleteNavigateTo: `/${orgId}/networking`,
    onRenamed: onConnectorRenamed,
    saveSuccessMessage: "Changes saved.",
  });

  if (settings.error && !settings.entity) {
    return <LoadError what="this connector" />;
  }

  if (!settings.entity) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-3 w-20 rounded bg-surface" />
        <div className="h-10 w-full rounded bg-surface" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={settings.handleSave} className="space-y-4">
        <div>
          <label
            htmlFor="connectorName"
            className="mb-1.5 block text-sm text-foreground"
          >
            Name
          </label>
          <TextInput
            id="connectorName"
            value={settings.name}
            onChange={(e) => settings.setName(e.target.value)}
            required
            className="w-full"
          />
        </div>
        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={
              settings.submitting || settings.name === settings.entity.name
            }
          >
            {settings.submitting ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>

      <div className="flex items-center justify-between gap-6 rounded-lg border border-danger/30 p-4">
        <div>
          <h2 className="text-sm font-semibold text-foreground">
            Delete this connector
          </h2>
          <p className="mt-0.5 max-w-md text-xs text-foreground/70">
            Deleting this connector will revoke its token and remove the route
            it provides to assigned resources.
          </p>
        </div>
        <Button
          variant="dangerOutline"
          onClick={settings.handleDelete}
          disabled={settings.submitting}
          className="shrink-0"
        >
          Delete connector
        </Button>
      </div>

      <Toast toast={settings.toast} />

      {settings.confirmDialog}
    </div>
  );
}
